import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Documents } from "@/components/ApplicationDetail";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { inr, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Search, FolderOpen, Grab, Undo2, Zap, ListChecks, FileCheck2 } from "lucide-react";

const SCOPES = [{ v: "all", l: "All" }, { v: "pool", l: "Unassigned pool" }, { v: "mine", l: "Mine" }, { v: "assigned", l: "Assigned" }];

function CoordDetail({ app, open, onOpenChange, canAct, defaultTab, onClaim, onSendBack, busy }) {
  const [note, setNote] = useState("");
  const history = app?.status_history || [];
  const claimed = !!(app?.coordinator_id || app?.coordinator_name);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white dark:bg-slate-900" data-testid="coord-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{app?.client_name || "Application"}</DialogTitle>
          <DialogDescription>{app?.loan_type} · {inr(app?.loan_amount)} · {app?.city || "—"}</DialogDescription>
        </DialogHeader>
        {app && (
          <Tabs defaultValue={defaultTab || "details"}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="details" data-testid="coord-tab-details"><ListChecks className="h-4 w-4 mr-1.5" />Details</TabsTrigger>
              <TabsTrigger value="documents" data-testid="coord-tab-documents"><FileCheck2 className="h-4 w-4 mr-1.5" />Documents</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="mt-3 space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><div className="text-xs uppercase tracking-wider text-slate-400">Phone</div><div className="font-num">{app.client_phone || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400">Status</div><StatusBadge status={app.status} /></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400">Telecaller</div><div>{app.telecaller_name || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400">Coordinator</div><div>{app.coordinator_name || "— (in pool)"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400">Employment</div><div>{app.employment_type || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400">Tenure</div><div className="font-num">{app.tenure_months ? `${app.tenure_months} mo` : "—"}</div></div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Status History ({history.length})</div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {history.length === 0 && <p className="text-sm text-slate-400">No history.</p>}
                  {history.map((h, i) => (
                    <div key={i} className="text-sm border-l-2 border-blue-200 dark:border-blue-900 pl-3 py-0.5">
                      <div className="flex justify-between"><span className="font-medium text-slate-800 dark:text-slate-200">{h.status}</span><span className="text-xs text-slate-400 font-num">{h.at ? formatDateTime(h.at) : ""}</span></div>
                      {h.note && <div className="text-xs text-slate-500">{h.note}</div>}
                    </div>
                  ))}
                </div>
              </div>
              {canAct && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-3 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Send back note (optional)</div>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for sending this file back to the telecaller…" data-testid="coord-sendback-note" />
                </div>
              )}
            </TabsContent>
            <TabsContent value="documents" className="mt-3">
              <Documents clientAppId={app.id} />
            </TabsContent>
          </Tabs>
        )}
        {canAct && app && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onSendBack(app, note)} disabled={busy} data-testid="coord-sendback-button"><Undo2 className="h-4 w-4 mr-1" />Send back</Button>
            {!claimed && <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => onClaim(app)} disabled={busy} data-testid="coord-claim-button"><Grab className="h-4 w-4 mr-1" />Claim</Button>}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CoordFiles() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canAct = ["Coordinator", "Admin"].includes(user?.role);
  const [scope, setScope] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("details");
  const [selected, setSelected] = useState({});

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ["coord-apps", scope],
    queryFn: async () => (await api.get("/coordinator/applications", { params: { scope } })).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["coord-apps"] });
  const resetSel = () => setSelected({});
  const openDetail = (app, tab = "details") => { setDetailTab(tab); setDetail(app); };

  const claim = useMutation({
    mutationFn: async (app) => (await api.post(`/applications/${app.id}/claim`, {})).data,
    onSuccess: () => { invalidate(); setDetail(null); toast.success("File claimed"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const claimOpen = useMutation({
    mutationFn: async (app) => { await api.post(`/applications/${app.id}/claim`, {}); return app; },
    onSuccess: (app) => { invalidate(); toast.success("Claimed — opening documents"); openDetail({ ...app, coordinator_name: user?.name }, "documents"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const sendBack = useMutation({
    mutationFn: async ({ app, note }) => {
      if (note && note.trim()) await api.post(`/applications/${app.id}/coordinator-feedback`, { note: note.trim() });
      return (await api.post(`/coordinator/applications/${app.id}/send-back`, {})).data;
    },
    onSuccess: () => { invalidate(); setDetail(null); toast.success("File sent back"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const bulkClaim = useMutation({
    mutationFn: async (ids) => Promise.all(ids.map((id) => api.post(`/applications/${id}/claim`, {}))),
    onSuccess: (_, ids) => { invalidate(); resetSel(); toast.success(`Claimed ${ids.length} file${ids.length === 1 ? "" : "s"}`); },
    onError: (e) => toast.error(apiError(e)),
  });
  const bulkSendBack = useMutation({
    mutationFn: async (ids) => (await api.post(`/coordinator/applications/bulk-action`, { ids, action: "send_back" })).data,
    onSuccess: (_, ids) => { invalidate(); resetSel(); toast.success(`Sent back ${ids.length} file${ids.length === 1 ? "" : "s"}`); },
    onError: (e) => toast.error(apiError(e)),
  });
  const busy = claim.isPending || claimOpen.isPending || sendBack.isPending || bulkClaim.isPending || bulkSendBack.isPending;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) => [a.client_name, a.client_phone, a.loan_type, a.telecaller_name, a.status].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [apps, search]);

  const selectableIds = useMemo(() => filtered.filter((a) => !(a.coordinator_id || a.coordinator_name)).map((a) => a.id), [filtered]);
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected[id]);
  const toggleAll = () => setSelected(allSelected ? {} : Object.fromEntries(selectableIds.map((id) => [id, true])));

  return (
    <div data-testid="coord-files-page">
      <PageHeader title="Coord Files" subtitle="Coordinator application workspace — claim from the pool and send files back, synced with the mobile app." />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="coord-search-input" placeholder="Search client, phone, status…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-slate-950" />
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-48 bg-white dark:bg-slate-950" data-testid="coord-scope-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{SCOPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{filtered.length} files</span>
      </div>

      {canAct && selectedIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 mb-4 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/30 px-4 py-2.5" data-testid="coord-bulk-bar">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => bulkSendBack.mutate(selectedIds)} data-testid="coord-bulk-sendback"><Undo2 className="h-4 w-4 mr-1" />Send back</Button>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" disabled={busy} onClick={() => bulkClaim.mutate(selectedIds)} data-testid="coord-bulk-claim"><Grab className="h-4 w-4 mr-1" />Claim selected</Button>
          </div>
        </div>
      )}

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="coord-files-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              {canAct && <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} disabled={selectableIds.length === 0} data-testid="coord-select-all" /></TableHead>}
              <TableHead className="uppercase text-xs tracking-wider">Client</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Phone</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Loan Type</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Amount</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Telecaller</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
              {canAct && <TableHead className="uppercase text-xs tracking-wider text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={canAct ? 8 : 6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && filtered.length === 0 && <TableRow><TableCell colSpan={canAct ? 8 : 6} className="text-center text-slate-400 py-10"><FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />No coordinator files found.</TableCell></TableRow>}
            {!isLoading && filtered.map((a) => {
              const claimed = !!(a.coordinator_id || a.coordinator_name);
              return (
                <TableRow key={a.id} data-testid={`coord-row-${a.id}`} onClick={() => openDetail(a)} className="cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                  {canAct && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={!!selected[a.id]} disabled={claimed} onCheckedChange={(v) => setSelected((s) => ({ ...s, [a.id]: !!v }))} data-testid={`coord-select-${a.id}`} /></TableCell>}
                  <TableCell className="font-medium text-slate-900 dark:text-slate-100">{a.client_name || "—"}</TableCell>
                  <TableCell className="font-num text-slate-600 dark:text-slate-300">{a.client_phone || "—"}</TableCell>
                  <TableCell className="text-slate-600 dark:text-slate-300">{a.loan_type || "—"}</TableCell>
                  <TableCell className="text-right font-num font-semibold">{inr(a.loan_amount)}</TableCell>
                  <TableCell className="text-slate-600 dark:text-slate-300">{a.telecaller_name || "—"}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  {canAct && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {!claimed
                        ? <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => claim.mutate(a)} data-testid={`coord-claim-${a.id}`}><Grab className="h-3.5 w-3.5 mr-1" />Claim</Button>
                            <Button size="sm" className="h-8 bg-blue-700 hover:bg-blue-800 text-white" disabled={busy} onClick={() => claimOpen.mutate(a)} data-testid={`coord-claim-open-${a.id}`}><Zap className="h-3.5 w-3.5 mr-1" />Claim & open</Button>
                          </div>
                        : <span className="text-xs text-slate-400">{a.coordinator_name || "Claimed"}</span>}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {detail && <CoordDetail app={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} canAct={canAct} defaultTab={detailTab} busy={busy} onClaim={(a) => claim.mutate(a)} onSendBack={(a, note) => sendBack.mutate({ app: a, note })} />}
    </div>
  );
}
